/**
 * Feature-specific helpers for the payout timesheet dashboard.
 */

/**
 * Formats decimal hours for compact dashboard display.
 *
 * @param {number|null|undefined} value - Decimal hour value.
 * @returns {string} Formatted hour string.
 */
export function formatDashboardHours(value) {
  if (value === null || value === undefined || Number(value) === 0) {
    return "-";
  }
  return Number(value).toFixed(2);
}

/**
 * Formats dashboard hour deltas with a plus sign for positive values.
 *
 * @param {number|null|undefined} value - Decimal hour delta.
 * @returns {string} Formatted delta string.
 */
export function formatDashboardDelta(value) {
  const formatted = formatDashboardHours(value);
  if (formatted === "-") {
    return formatted;
  }
  return Number(value) > 0 ? `+${formatted}` : formatted;
}

/**
 * Formats whole-number dashboard counts with a dash for empty values.
 *
 * @param {number|null|undefined} value - Count value.
 * @returns {string|number} Formatted count display.
 */
export function formatDashboardCount(value) {
  if (value === null || value === undefined || Number(value) === 0) {
    return "-";
  }
  return value;
}

/**
 * Returns the first and last ISO date strings for a YYYY-MM month value.
 *
 * @param {string} monthValue - Month value in YYYY-MM format.
 * @returns {{start: string, end: string}} Month date range.
 */
export function getMonthDateRange(monthValue) {
  const [year, month] = monthValue.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);

  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

/**
 * Builds recent month options for the dashboard selector.
 *
 * @param {number} count - Number of months to include.
 * @returns {Array<{value: string, label: string}>} Month options.
 */
export function getRecentMonthOptions(count = 12) {
  const monthFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
  });
  const fullFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  });
  const today = new Date();

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: fullFormatter.format(date),
      month: String(date.getMonth() + 1).padStart(2, "0"),
      monthLabel: monthFormatter.format(date),
      year: String(date.getFullYear()),
    };
  });
}

/**
 * Returns status-chip metadata for a payroll week.
 *
 * @param {Object} statuses - Payroll status object from the API.
 * @returns {Array<{key: string, label: string, complete: boolean}>} Status chip items.
 */
export function getPayrollStatusItems(statuses = {}) {
  return [
    {
      key: "iifExported",
      label: "QuickBooks Exported",
      complete: Boolean(statuses.iifExported),
    },
    {
      key: "timesheetsEmailed",
      label: "Timesheets Emailed",
      complete: Boolean(statuses.timesheetsEmailed),
    },
    {
      key: "signInSheetsGenerated",
      label: "Sign-In Sheets Complete",
      complete: Boolean(statuses.signInSheetsGenerated),
    },
    {
      key: "safetyMeetingFormsGenerated",
      label: "Safety Meetings Complete",
      complete: Boolean(statuses.safetyMeetingFormsGenerated),
    },
  ];
}
